import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { mfaApi } from '../../services/mfaApi';
import { Button } from '../../ui';
import { MfaCodeInput } from '../../components/mfa/MfaCodeInput';
import type { MfaChallengeMethod } from '../../types/auth';
import './auth.css';

const METHOD_LABELS: Record<MfaChallengeMethod, string> = {
  totp: 'Authenticator app',
  email: 'Email code',
  recovery_code: 'Recovery code',
};

export function MfaChallengePage() {
  const { pendingMfaChallenge, completeMfaChallenge, clearMfaChallenge, isAuthenticated } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  // The methods array drives both the UI and the request payload. Default to
  // the first listed method (TOTP first in the API response).
  const availableMethods = useMemo<MfaChallengeMethod[]>(
    () => pendingMfaChallenge?.methods ?? ['totp'],
    [pendingMfaChallenge]
  );
  const [method, setMethod] = useState<MfaChallengeMethod>(availableMethods[0] ?? 'totp');
  const [code, setCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | undefined>(undefined);

  // If the page was loaded directly without a pending challenge, send the
  // user back to /login. Skip if already authenticated — that means we just
  // finished a successful verify and the onSuccess handler is navigating to `from`.
  useEffect(() => {
    if (!pendingMfaChallenge && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [pendingMfaChallenge, isAuthenticated, navigate]);

  // Recovery codes look like dashed alphanumerics — we don't need a 6-digit
  // grid for them. The verify endpoint distinguishes by `method`.
  const useDigitGrid = method === 'totp' || method === 'email';

  const verifyMutation = useMutation({
    mutationFn: async () => {
      if (!pendingMfaChallenge) throw new Error('Missing MFA challenge');
      return mfaApi.verifyChallenge({
        mfa_token: pendingMfaChallenge.mfaToken,
        code,
        method: method,
      });
    },
    onSuccess: async response => {
      if (response.success) {
        await completeMfaChallenge();
        navigate(from, { replace: true });
      } else {
        setRemaining(response.remaining_attempts);
        setError(response.error || 'Invalid code');
      }
    },
    onError: (err: Error) => setError(err.message),
  });

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      if (!pendingMfaChallenge) throw new Error('Missing MFA challenge');
      return mfaApi.sendEmailCode({ mfa_token: pendingMfaChallenge.mfaToken });
    },
    onSuccess: response => {
      if (response.success) {
        setEmailSent(true);
        setError(null);
      } else {
        setError(response.error || 'Failed to send email code');
      }
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!code) {
      setError('Enter your code.');
      return;
    }
    setError(null);
    verifyMutation.mutate();
  };

  const handleSwitchMethod = (next: MfaChallengeMethod) => {
    setMethod(next);
    setCode('');
    setError(null);
    setEmailSent(false);
  };

  const handleCancel = () => {
    clearMfaChallenge();
    navigate('/login', { replace: true });
  };

  if (!pendingMfaChallenge) return null;

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>
          <ShieldCheck size={20} aria-hidden="true" /> Two-factor authentication
        </h1>
        <p className="auth-subtitle">Enter a verification code to finish signing in.</p>

        {availableMethods.length > 1 && (
          <div className="mfa-challenge__methods" role="tablist" aria-label="Verification method">
            {availableMethods.map(m => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={method === m}
                className={`mfa-challenge__method ${method === m ? 'is-active' : ''}`}
                onClick={() => handleSwitchMethod(m)}
              >
                {METHOD_LABELS[m]}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {method === 'email' && !emailSent && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => sendEmailMutation.mutate()}
              loading={sendEmailMutation.isPending}
            >
              Send code to my email
            </Button>
          )}
          {method === 'email' && emailSent && (
            <p className="auth-info">A verification code was sent to your email.</p>
          )}

          {useDigitGrid ? (
            <MfaCodeInput value={code} onChange={setCode} autoFocus />
          ) : (
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="Recovery code"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              className="mfa-challenge__recovery-input"
            />
          )}

          {error && (
            <div className="auth-error">
              {error}
              {remaining !== undefined && remaining >= 0 && (
                <span> ({remaining} attempts left)</span>
              )}
            </div>
          )}

          <Button type="submit" block size="lg" loading={verifyMutation.isPending}>
            Verify
          </Button>
        </form>

        <div className="auth-footer">
          <button type="button" className="auth-link" onClick={handleCancel}>
            Use a different account
          </button>
        </div>
      </div>
    </div>
  );
}
